import { RIYAL } from "@/lib/currency";
import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  User,
  CreditCard,
  Hash,
  ExternalLink,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/lib/formatters";
import { displayEmail, isPlaceholderEmail } from "@/lib/email";
import type { BilledToInfo, ReceiptDetail } from "@/types/database";

export interface ReceiptDocumentCompany {
  name: string | null;
  address: string | null;
  cr_number: string | null;
  vat_number: string | null;
}

export interface ReceiptDocumentProps {
  receipt: ReceiptDetail;
  company: ReceiptDocumentCompany | null | undefined;
  billedTo: BilledToInfo | null;
  channelLabel: string;
  typeLabel: string;
  formatDate: (dateStr: string | null | undefined) => string;
  /**
   * Portal base path (e.g. "/dashboard", "/portal/owner") for the
   * property/unit/contract links. `null` renders plain text instead —
   * used on the public (unauthenticated) receipt page, which has no
   * portal to link into (mirrors InvoicePublicView's detailsSlot: text
   * only, no nav links, on the public surface).
   */
  portalBase: string | null;
}

/**
 * ReceiptDocument — the printable/visual body of a payment receipt (سند قبض),
 * shared between ReceiptDetailsModal (dashboard) and ReceiptPublicView (the
 * server-side PDF render target at /receipt/:id) — the same "one document,
 * many render sites" shape as InvoiceDocument for invoices.
 */
export const ReceiptDocument = forwardRef<HTMLDivElement, ReceiptDocumentProps>(
  ({ receipt, company, billedTo, channelLabel, typeLabel, formatDate, portalBase }, ref) => {
    const { t } = useLanguage();

    const propertyNode = receipt.property?.name ? (
      portalBase ? (
        <Link
          to={`${portalBase}/properties/${receipt.property_id}`}
          className="font-medium text-primary hover:underline flex items-center gap-1"
        >
          {receipt.property.name}
          <ExternalLink className="h-3 w-3 rtl:rotate-180 no-print" />
        </Link>
      ) : (
        <span className="font-medium text-gray-700 text-xs">{receipt.property.name}</span>
      )
    ) : (
      <span className="font-medium">---</span>
    );

    const unitNode = receipt.unit?.unit_number ? (
      portalBase ? (
        <Link
          to={`${portalBase}/properties/${receipt.property_id}/units/${receipt.unit_id}`}
          className="font-medium text-primary hover:underline flex items-center gap-1"
        >
          {receipt.unit.unit_number}
          <ExternalLink className="h-3 w-3 rtl:rotate-180 no-print" />
        </Link>
      ) : (
        <span className="font-medium text-gray-700 text-xs">{receipt.unit.unit_number}</span>
      )
    ) : null;

    const contractNode = receipt.contract?.contract_number ? (
      portalBase ? (
        <Link
          to={`${portalBase}/contracts/${receipt.contract_id}`}
          className="font-medium text-primary hover:underline flex items-center gap-1"
        >
          #{receipt.contract.contract_number}
          <ExternalLink className="h-3 w-3 rtl:rotate-180 no-print" />
        </Link>
      ) : (
        <span className="font-medium text-gray-700 text-xs">#{receipt.contract.contract_number}</span>
      )
    ) : null;

    return (
      // receipt-print-area: the waitForSelector the server-side PDF renderer
      // (receipt-pdf.service.ts) waits for before printing — same role as
      // InvoiceDocument's .invoice-print-area.
      <div ref={ref} className="receipt-print-area space-y-6 py-4">
        {/* Header Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Received By (Company) */}
          <div className="space-y-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              {t("مستلم المبلغ", "Received By")}
            </h3>
            <div className="bg-muted/50 rounded-lg p-4 space-y-1">
              <p className="font-semibold">{company?.name || "---"}</p>
              <p className="text-sm text-muted-foreground">{company?.address || "---"}</p>
              {company?.cr_number && (
                <p className="text-sm text-muted-foreground">{t("س.ت", "CR")} {company.cr_number}</p>
              )}
              {company?.vat_number && (
                <p className="text-sm text-muted-foreground">{t("الرقم الضريبي", "VAT")} {company.vat_number}</p>
              )}
            </div>
          </div>

          {/* Receipt Meta */}
          <div className="space-y-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Hash className="h-4 w-4 text-primary" />
              {t("بيانات سند القبض", "Receipt Details")}
            </h3>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              {receipt.invoice?.due_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("تاريخ الاستحقاق", "Due Date")}</span>
                  <span className="font-medium">{formatDate(receipt.invoice.due_date)}</span>
                </div>
              )}
              {receipt.invoice?.invoice_number && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("رقم الفاتورة", "Invoice ID")}</span>
                  <span className="font-medium">{receipt.invoice.invoice_number}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("رقم الإيصال", "Receipt Number")}</span>
                <span className="font-medium">{receipt.receipt_number || "---"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("تاريخ الدفع", "Payment Date")}</span>
                <span className="font-medium">{formatDate(receipt.payment_date)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("قناة الدفع", "Payment Channel")}</span>
                <span className="font-medium flex items-center gap-1">
                  <CreditCard className="h-3 w-3" />
                  {channelLabel}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Payer & Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Received From (Tenant or Owner / Payer) */}
          <div className="space-y-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              {t("المستلم منه (الدافع)", "Received From (Payer)")}
              {billedTo?.type === "owner" && (
                <Badge variant="outline" className="text-xs">{t("مالك", "Owner")}</Badge>
              )}
              {billedTo?.type === "tenant" && (
                <Badge variant="outline" className="text-xs">{t("مستأجر", "Tenant")}</Badge>
              )}
            </h3>
            <div className="bg-muted/50 rounded-lg p-4 space-y-1">
              <p className="font-semibold">{billedTo?.name || receipt.tenant?.full_name || "---"}</p>
              {billedTo?.nameEn && billedTo.nameEn !== billedTo.name && (
                <p className="text-sm text-muted-foreground">{billedTo.nameEn}</p>
              )}
              {(billedTo?.phone || receipt.tenant?.phone) && (
                <p className="text-sm text-muted-foreground">{billedTo?.phone || receipt.tenant?.phone}</p>
              )}
              {(!isPlaceholderEmail(billedTo?.email) || !isPlaceholderEmail(receipt.tenant?.email)) && (
                <p className="text-sm text-muted-foreground">
                  {displayEmail(billedTo?.email) || displayEmail(receipt.tenant?.email)}
                </p>
              )}
              {(billedTo?.address || receipt.tenant?.address) && (
                <p className="text-sm text-muted-foreground">{billedTo?.address || receipt.tenant?.address}</p>
              )}
              {billedTo?.entityType === "commercial" && billedTo?.crNumber && (
                <p className="text-sm text-muted-foreground font-medium">{t("س.ت", "CR No.")} {billedTo.crNumber}</p>
              )}
              {billedTo?.idNumber && (
                <p className="text-sm text-muted-foreground">
                  {billedTo.idType === "national_id" ? t("هوية وطنية", "National ID") :
                   billedTo.idType === "iqama" ? t("إقامة", "Iqama") :
                   billedTo.idType === "passport" ? t("جواز سفر", "Passport") :
                   t("رقم الهوية", "ID No.")}
                  {" "}{billedTo.idNumber}
                </p>
              )}
            </div>
          </div>

          {/* Property Details */}
          <div className="space-y-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              {t("التفاصيل", "Details")}
            </h3>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("العقار", "Property")}</span>
                {propertyNode}
              </div>
              {unitNode && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("الوحدة", "Unit")}</span>
                  {unitNode}
                </div>
              )}
              {contractNode && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("العقد", "Contract")}</span>
                  {contractNode}
                </div>
              )}
              {receipt.reference_number && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("رقم المرجع", "Reference")}</span>
                  <span className="font-medium">#{receipt.reference_number}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("نوع الدفعة", "Payment Type")}</span>
                <span className="font-medium">{typeLabel}</span>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Amount Summary */}
        <div className="bg-primary/10 border-2 border-primary rounded-xl p-6">
          <div className="text-center mb-4">
            <p className="text-sm text-primary mb-1">{t("المبلغ المدفوع", "Amount Paid")}</p>
            <p className="text-3xl font-bold text-primary">
              {formatNumber(receipt.amount)}&nbsp;{RIYAL}
            </p>
          </div>
          {receipt.invoice?.total_amount && (
            <div className="border-t border-primary/20 pt-3 mt-3">
              <div className="flex justify-between text-sm">
                <span className="text-primary/70">{t("إجمالي الفاتورة", "Invoice Total")}</span>
                <span className="font-medium text-primary">{formatNumber(receipt.invoice.total_amount)}&nbsp;{RIYAL}</span>
              </div>
            </div>
          )}
        </div>

        {/* Payment Details Summary */}
        <div className="bg-muted/30 rounded-lg p-4 space-y-2">
          <h3 className="font-semibold text-base mb-3">{t("بيانات الدفع", "Payment Information")}</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("طريقة الدفع", "Payment Method")}</span>
              <span className="font-medium flex items-center gap-1">
                <CreditCard className="h-3 w-3" />
                {channelLabel}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("تاريخ الدفع", "Payment Date")}</span>
              <span className="font-medium">{formatDate(receipt.payment_date)}</span>
            </div>
            {receipt.reference_number && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("رقم المرجع", "Reference No.")}</span>
                <span className="font-medium">#{receipt.reference_number}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("رقم سند القبض", "Receipt No.")}</span>
              <span className="font-medium">{receipt.receipt_number || "---"}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {receipt.notes && (
          <>
            <Separator />
            <div className="space-y-2">
              <h3 className="font-semibold text-base">{t("ملاحظات", "Notes")}</h3>
              <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">
                {receipt.notes}
              </p>
            </div>
          </>
        )}
      </div>
    );
  },
);

ReceiptDocument.displayName = "ReceiptDocument";
